/**
 * Amazon Cognito Configuration and Service
 *
 * This module provides Cognito authentication functionality:
 * - User Pool configuration
 * - Authentication (sign in, sign up, sign out)
 * - Token management
 * - Social login (Google, Apple)
 * - Password reset and email verification
 */
import {
  AuthenticationDetails,
  CognitoUser,
  CognitoUserAttribute,
  CognitoUserPool,
  CognitoUserSession,
} from 'amazon-cognito-identity-js';

// ============= Configuration =============

/**
 * Cognito User Pool configuration from environment variables
 */
export const cognitoConfig = {
  userPoolId: import.meta.env.VITE_COGNITO_USER_POOL_ID || '',
  clientId: import.meta.env.VITE_COGNITO_CLIENT_ID || '',
  region: import.meta.env.VITE_COGNITO_REGION || 'us-east-1',
  domain: import.meta.env.VITE_COGNITO_DOMAIN || '',
};

// Validate configuration
const isCognitoConfigured = cognitoConfig.userPoolId && cognitoConfig.clientId;

// Configuration validated at runtime when getUserPool is called

/**
 * Lazy-loaded User Pool instance to avoid crashes when config is missing
 */
let _userPool: CognitoUserPool | null = null;

const getUserPool = (): CognitoUserPool => {
  if (!isCognitoConfigured) {
    throw new Error(
      'Cognito is not configured. Set VITE_COGNITO_USER_POOL_ID and VITE_COGNITO_CLIENT_ID environment variables.'
    );
  }
  if (!_userPool) {
    _userPool = new CognitoUserPool({
      UserPoolId: cognitoConfig.userPoolId,
      ClientId: cognitoConfig.clientId,
    });
  }
  return _userPool;
};

// Legacy export for backwards compatibility
const userPool = isCognitoConfigured
  ? new CognitoUserPool({
      UserPoolId: cognitoConfig.userPoolId,
      ClientId: cognitoConfig.clientId,
    })
  : (null as unknown as CognitoUserPool);

// ============= Types =============

export interface CognitoTokens {
  idToken: string;
  accessToken: string;
  refreshToken: string;
}

export interface CognitoUserInfo {
  sub: string;
  email: string;
  name?: string;
  email_verified?: boolean;
  picture?: string;
  phone_number?: string;
}

export interface SignUpParams {
  email: string;
  password: string;
  name: string;
  locale?: string;
}

export interface SignInParams {
  email: string;
  password: string;
}

export interface ConfirmSignUpParams {
  email: string;
  code: string;
}

export interface ForgotPasswordParams {
  email: string;
}

export interface ConfirmForgotPasswordParams {
  email: string;
  code: string;
  newPassword: string;
}

// ============= Passkey Types =============

export interface PasskeyCredential {
  credentialId: string;
  friendlyName?: string;
  createdAt: Date;
  authenticatorAttachment?: 'platform' | 'cross-platform';
}

export interface PasskeySignInResult {
  tokens: CognitoTokens;
  session: string;
}

// ============= Authentication Functions =============

/**
 * Sign up a new user with email and password
 */
export const signUp = (params: SignUpParams): Promise<CognitoUser> => {
  return new Promise((resolve, reject) => {
    const attributeList = [
      new CognitoUserAttribute({ Name: 'email', Value: params.email }),
      new CognitoUserAttribute({ Name: 'name', Value: params.name }),
    ];

    // Add locale attribute if provided (for localized verification emails)
    if (params.locale) {
      attributeList.push(
        new CognitoUserAttribute({ Name: 'locale', Value: params.locale })
      );
    }

    getUserPool().signUp(
      params.email,
      params.password,
      attributeList,
      [],
      (err, result) => {
        if (err) {
          reject(err);
          return;
        }
        if (!result) {
          reject(new Error('Sign up failed: no result returned'));
          return;
        }
        resolve(result.user);
      }
    );
  });
};

/**
 * Confirm sign up with verification code sent to email
 */
export const confirmSignUp = (params: ConfirmSignUpParams): Promise<void> => {
  return new Promise((resolve, reject) => {
    const cognitoUser = new CognitoUser({
      Username: params.email,
      Pool: getUserPool(),
    });

    cognitoUser.confirmRegistration(params.code, true, (err, _result) => {
      if (err) {
        reject(err);
        return;
      }
      resolve();
    });
  });
};

/**
 * Resend confirmation code for sign up
 */
export const resendConfirmationCode = (email: string): Promise<void> => {
  return new Promise((resolve, reject) => {
    const cognitoUser = new CognitoUser({
      Username: email,
      Pool: getUserPool(),
    });

    cognitoUser.resendConfirmationCode((err, _result) => {
      if (err) {
        reject(err);
        return;
      }
      resolve();
    });
  });
};

/**
 * Sign in with email and password
 */
export const signIn = (
  params: SignInParams
): Promise<{ session: CognitoUserSession; user: CognitoUser }> => {
  return new Promise((resolve, reject) => {
    const authenticationDetails = new AuthenticationDetails({
      Username: params.email,
      Password: params.password,
    });

    const cognitoUser = new CognitoUser({
      Username: params.email,
      Pool: getUserPool(),
    });

    cognitoUser.authenticateUser(authenticationDetails, {
      onSuccess: session => {
        resolve({ session, user: cognitoUser });
      },
      onFailure: err => {
        reject(err);
      },
      newPasswordRequired: () => {
        reject(
          new Error('New password required. Please complete password setup.')
        );
      },
      mfaRequired: () => {
        // For now, reject MFA - can be extended later
        reject(new Error('MFA is required but not yet implemented'));
      },
      mfaSetup: () => {
        reject(new Error('MFA setup is required'));
      },
    });
  });
};

/**
 * Sign out the current user
 */
export const signOut = (): Promise<void> => {
  return new Promise(resolve => {
    const cognitoUser = getUserPool().getCurrentUser();
    if (cognitoUser) {
      cognitoUser.signOut();
    }
    resolve();
  });
};

/**
 * Sign out from all devices (global sign out)
 */
export const globalSignOut = (): Promise<void> => {
  return new Promise((resolve, reject) => {
    const cognitoUser = getUserPool().getCurrentUser();
    if (!cognitoUser) {
      resolve();
      return;
    }

    cognitoUser.getSession(
      (err: Error | null, session: CognitoUserSession | null) => {
        if (err || !session) {
          // No valid session, just do local signout
          cognitoUser.signOut();
          resolve();
          return;
        }

        cognitoUser.globalSignOut({
          onSuccess: () => resolve(),
          onFailure: err => {
            // Even if global signout fails, do local signout
            cognitoUser.signOut();
            reject(err);
          },
        });
      }
    );
  });
};

/**
 * Get the current user's session (tokens)
 */
export const getCurrentSession = (): Promise<CognitoUserSession | null> => {
  if (!isCognitoConfigured) {
    return Promise.resolve(null);
  }
  return new Promise((resolve, reject) => {
    const cognitoUser = getUserPool().getCurrentUser();
    if (!cognitoUser) {
      resolve(null);
      return;
    }

    cognitoUser.getSession(
      (err: Error | null, session: CognitoUserSession | null) => {
        if (err) {
          reject(err);
          return;
        }
        resolve(session);
      }
    );
  });
};

/**
 * Get tokens from the current session
 */
export const getTokens = async (): Promise<CognitoTokens | null> => {
  const session = await getCurrentSession();
  if (!session) {
    return null;
  }

  return {
    idToken: session.getIdToken().getJwtToken(),
    accessToken: session.getAccessToken().getJwtToken(),
    refreshToken: session.getRefreshToken().getToken(),
  };
};

/**
 * Get the current ID token (for API calls)
 */
export const getIdToken = async (): Promise<string | null> => {
  const tokens = await getTokens();
  return tokens?.idToken || null;
};

/**
 * Parse user info from the ID token
 */
export const getUserInfoFromToken = (
  idToken: string
): CognitoUserInfo | null => {
  try {
    const payload = idToken.split('.')[1];
    const decoded = JSON.parse(atob(payload));
    return {
      sub: decoded.sub,
      email: decoded.email,
      name: decoded.name,
      email_verified: decoded.email_verified,
      picture: decoded.picture,
      phone_number: decoded.phone_number,
    };
  } catch {
    return null;
  }
};

/**
 * Get current authenticated user info
 */
export const getCurrentUser = async (): Promise<CognitoUserInfo | null> => {
  const tokens = await getTokens();
  if (!tokens) {
    return null;
  }
  return getUserInfoFromToken(tokens.idToken);
};

/**
 * Check if a user is a social-only user (no password) by parsing the JWT token.
 * Social-only users signed up with Google/Apple and never set a password.
 * Users who signed up with email/password (even if they later linked social) can change password.
 *
 * @param idToken - The JWT ID token to check
 * @returns true if user is social-only, false if they have a password
 */
export const isSocialOnlyUserFromToken = (idToken: string): boolean => {
  try {
    const payload = idToken.split('.')[1];
    const decoded = JSON.parse(atob(payload));

    // If identities exists and has entries, user is a social-only user
    // Email/password users don't have the identities field
    const identities = decoded.identities;

    if (!identities || identities.length === 0) {
      // No federated identities = email/password user
      return false;
    }

    // User has federated identities - they're a social-only user
    return true;
  } catch {
    return false;
  }
};

// ============= Password Functions =============

/**
 * Initiate forgot password flow
 */
export const forgotPassword = (params: ForgotPasswordParams): Promise<void> => {
  return new Promise((resolve, reject) => {
    const cognitoUser = new CognitoUser({
      Username: params.email,
      Pool: getUserPool(),
    });

    cognitoUser.forgotPassword({
      onSuccess: () => resolve(),
      onFailure: err => reject(err),
    });
  });
};

/**
 * Complete forgot password with code and new password
 */
export const confirmForgotPassword = (
  params: ConfirmForgotPasswordParams
): Promise<void> => {
  return new Promise((resolve, reject) => {
    const cognitoUser = new CognitoUser({
      Username: params.email,
      Pool: getUserPool(),
    });

    cognitoUser.confirmPassword(params.code, params.newPassword, {
      onSuccess: () => resolve(),
      onFailure: err => reject(err),
    });
  });
};

/**
 * Change password for authenticated user
 */
export const changePassword = (
  oldPassword: string,
  newPassword: string
): Promise<void> => {
  return new Promise((resolve, reject) => {
    const cognitoUser = getUserPool().getCurrentUser();
    if (!cognitoUser) {
      reject(new Error('No authenticated user'));
      return;
    }

    cognitoUser.getSession(
      (err: Error | null, session: CognitoUserSession | null) => {
        if (err || !session) {
          reject(err || new Error('No valid session'));
          return;
        }

        cognitoUser.changePassword(oldPassword, newPassword, err => {
          if (err) {
            reject(err);
            return;
          }
          resolve();
        });
      }
    );
  });
};

// ============= Social Login (OAuth) =============

/**
 * OAuth providers
 */
export type OAuthProvider = 'Google' | 'SignInWithApple';

/**
 * Get the OAuth sign-in URL for a provider
 * @param provider - The OAuth provider (Google or SignInWithApple)
 * @param state - Optional state parameter to pass through the OAuth flow (e.g., invite token)
 */
export const getOAuthSignInUrl = (
  provider: OAuthProvider,
  state?: string
): string => {
  const redirectUri = `${window.location.origin}/auth/callback`;
  const responseType = 'code';
  const scope = 'openid email profile';

  const params = new URLSearchParams({
    client_id: cognitoConfig.clientId,
    response_type: responseType,
    scope: scope,
    redirect_uri: redirectUri,
    identity_provider: provider,
  });

  // Add state parameter if provided (used to preserve invite token through OAuth flow)
  if (state) {
    params.set('state', state);
  }

  return `https://${cognitoConfig.domain}/oauth2/authorize?${params.toString()}`;
};

export interface OAuthState {
  invite?: string;
  lang?: string;
  provider?: 'google' | 'apple';
}

/**
 * Sign in with Google
 * @param state - Optional state to preserve through the OAuth flow (invite token, language)
 */
export const signInWithGoogle = (state?: OAuthState): void => {
  const stateWithProvider = { ...state, provider: 'google' as const };
  const stateParam = JSON.stringify(stateWithProvider);
  window.location.href = getOAuthSignInUrl('Google', stateParam);
};

/**
 * Sign in with Apple
 * @param state - Optional state to preserve through the OAuth flow (invite token, language)
 */
export const signInWithApple = (state?: OAuthState): void => {
  const stateWithProvider = { ...state, provider: 'apple' as const };
  const stateParam = JSON.stringify(stateWithProvider);
  window.location.href = getOAuthSignInUrl('SignInWithApple', stateParam);
};

/**
 * Exchange OAuth authorization code for tokens
 */
export const exchangeCodeForTokens = async (
  code: string
): Promise<CognitoTokens> => {
  const redirectUri = `${window.location.origin}/auth/callback`;

  const params = new URLSearchParams({
    grant_type: 'authorization_code',
    client_id: cognitoConfig.clientId,
    code: code,
    redirect_uri: redirectUri,
  });

  const response = await fetch(`https://${cognitoConfig.domain}/oauth2/token`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    body: params.toString(),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(
      error.error_description || 'Failed to exchange code for tokens'
    );
  }

  const data = await response.json();

  return {
    idToken: data.id_token,
    accessToken: data.access_token,
    refreshToken: data.refresh_token,
  };
};

// ============= Token Refresh =============

/**
 * Refresh the session tokens
 */
export const refreshSession = (): Promise<CognitoUserSession> => {
  return new Promise((resolve, reject) => {
    const cognitoUser = getUserPool().getCurrentUser();
    if (!cognitoUser) {
      reject(new Error('No authenticated user'));
      return;
    }

    cognitoUser.getSession(
      (err: Error | null, session: CognitoUserSession | null) => {
        if (err || !session) {
          reject(err || new Error('No valid session'));
          return;
        }

        const refreshToken = session.getRefreshToken();
        cognitoUser.refreshSession(refreshToken, (err, newSession) => {
          if (err) {
            reject(err);
            return;
          }
          resolve(newSession);
        });
      }
    );
  });
};

// ============= Utility Functions =============

/**
 * Check if the user is authenticated
 */
export const isAuthenticated = async (): Promise<boolean> => {
  if (!isCognitoConfigured) {
    return false;
  }
  try {
    const session = await getCurrentSession();
    return session?.isValid() || false;
  } catch {
    return false;
  }
};

/**
 * Check if the current session is valid (not expired)
 */
export const isSessionValid = async (): Promise<boolean> => {
  if (!isCognitoConfigured) {
    return false;
  }
  try {
    const session = await getCurrentSession();
    return session?.isValid() || false;
  } catch {
    return false;
  }
};

// ============= Token Expiration & Refresh =============

/**
 * Check if a JWT token is expired
 * @param token - JWT token string
 * @returns true if token is expired or invalid
 */
export const isTokenExpired = (token: string): boolean => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    // If no exp claim, treat as expired for safety
    if (typeof payload.exp !== 'number') {
      return true;
    }
    // Add 30 second buffer to account for clock skew
    return payload.exp * 1000 < Date.now() + 30000;
  } catch {
    return true;
  }
};

/**
 * Custom error for invalid/expired refresh tokens
 * Used to distinguish from network errors for offline support
 */
export class RefreshTokenInvalidError extends Error {
  constructor() {
    super('Refresh token is invalid or expired');
    this.name = 'RefreshTokenInvalidError';
  }
}

/**
 * Refresh tokens using Cognito's OAuth token endpoint
 * Works for both OAuth (Google/Apple) and email/password users
 * @param refreshToken - The refresh token to use
 * @returns New tokens (idToken, accessToken, refreshToken)
 * @throws RefreshTokenInvalidError if token is revoked/expired
 * @throws Error for network errors (keeps user logged in offline)
 */
export const refreshTokensWithEndpoint = async (
  refreshToken: string
): Promise<CognitoTokens> => {
  let response: Response;

  try {
    response = await fetch(`https://${cognitoConfig.domain}/oauth2/token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams({
        grant_type: 'refresh_token',
        client_id: cognitoConfig.clientId,
        refresh_token: refreshToken,
      }).toString(),
    });
  } catch {
    // Network error (offline) - throw generic error, NOT RefreshTokenInvalidError
    // This allows offline users to keep using cached data
    throw new Error('Network error during token refresh');
  }

  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    // Cognito returns 'invalid_grant' when refresh token is revoked/expired
    if (data.error === 'invalid_grant') {
      throw new RefreshTokenInvalidError();
    }
    throw new Error('Token refresh failed');
  }

  const data = await response.json();

  // Validate response contains expected tokens
  if (!data.id_token || !data.access_token) {
    throw new Error('Invalid token response from Cognito');
  }

  return {
    idToken: data.id_token,
    accessToken: data.access_token,
    refreshToken: refreshToken, // Cognito reuses same refresh token
  };
};

// ============= Passkey (WebAuthn) Functions =============

/**
 * Helper to make Cognito API calls directly
 */
const cognitoApiCall = async <T>(
  action: string,
  params: Record<string, unknown>
): Promise<T> => {
  const response = await fetch(
    `https://cognito-idp.${cognitoConfig.region}.amazonaws.com/`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-amz-json-1.1',
        'X-Amz-Target': `AWSCognitoIdentityProviderService.${action}`,
      },
      body: JSON.stringify(params),
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || `Cognito API error: ${action}`);
  }

  return response.json();
};

/**
 * Check if WebAuthn/Passkeys are supported in the current browser
 */
export const isPasskeySupported = (): boolean => {
  return (
    typeof window !== 'undefined' &&
    typeof window.PublicKeyCredential !== 'undefined' &&
    typeof navigator.credentials !== 'undefined'
  );
};

// ============= Passkey Email Storage =============
// Store user's email when they register a passkey so we can use it for usernameless sign-in
// Email is base64 encoded for basic obfuscation (not security, just prevents casual snooping)

const PASSKEY_EMAIL_KEY = 'passkey_user_ref';

/**
 * Save the user's email for passkey sign-in (base64 encoded)
 * Called when a passkey is registered
 */
export const savePasskeyEmail = (email: string): void => {
  try {
    const encoded = btoa(email);
    localStorage.setItem(PASSKEY_EMAIL_KEY, encoded);
  } catch {
    // Ignore localStorage errors
  }
};

/**
 * Get the stored email for passkey sign-in (base64 decoded)
 * Returns null if not found or invalid
 */
export const getPasskeyEmail = (): string | null => {
  try {
    const encoded = localStorage.getItem(PASSKEY_EMAIL_KEY);
    if (!encoded) return null;
    return atob(encoded);
  } catch {
    return null;
  }
};

/**
 * Clear the stored passkey email (e.g., on logout)
 */
export const clearPasskeyEmail = (): void => {
  try {
    localStorage.removeItem(PASSKEY_EMAIL_KEY);
  } catch {
    // Ignore localStorage errors
  }
};

/**
 * Start passkey registration - gets credential creation options from Cognito
 */
export const startPasskeyRegistration = async (
  accessToken: string
): Promise<PublicKeyCredentialCreationOptions> => {
  // Cognito returns the options directly in CredentialCreationOptions (not nested in publicKey)
  const response = await cognitoApiCall<{
    CredentialCreationOptions?: {
      challenge: string;
      rp: { id: string; name: string };
      user: { id: string; name: string; displayName: string };
      pubKeyCredParams: Array<{ type: string; alg: number }>;
      timeout?: number;
      excludeCredentials?: Array<{
        id: string;
        type: string;
        transports?: string[];
      }>;
      authenticatorSelection?: {
        authenticatorAttachment?: string;
        residentKey?: string;
        requireResidentKey?: boolean;
        userVerification?: string;
      };
      attestation?: string;
    };
  }>('StartWebAuthnRegistration', {
    AccessToken: accessToken,
  });

  if (!response.CredentialCreationOptions) {
    throw new Error(
      'WebAuthn registration not available. Please ensure passkeys are enabled for this user pool.'
    );
  }

  const options = response.CredentialCreationOptions;

  if (!options.challenge) {
    throw new Error(
      'Invalid credential creation options received from Cognito.'
    );
  }

  // Convert base64url strings to ArrayBuffer
  return {
    challenge: base64UrlToArrayBuffer(options.challenge),
    rp: options.rp,
    user: {
      id: base64UrlToArrayBuffer(options.user.id),
      name: options.user.name,
      displayName: options.user.displayName,
    },
    pubKeyCredParams: options.pubKeyCredParams.map(p => ({
      type: p.type as PublicKeyCredentialType,
      alg: p.alg,
    })),
    timeout: options.timeout,
    excludeCredentials: options.excludeCredentials?.map(c => ({
      id: base64UrlToArrayBuffer(c.id),
      type: c.type as PublicKeyCredentialType,
      transports: c.transports as AuthenticatorTransport[] | undefined,
    })),
    authenticatorSelection: options.authenticatorSelection
      ? {
          authenticatorAttachment: options.authenticatorSelection
            .authenticatorAttachment as AuthenticatorAttachment | undefined,
          residentKey: options.authenticatorSelection.residentKey as
            ResidentKeyRequirement | undefined,
          requireResidentKey: options.authenticatorSelection.requireResidentKey,
          userVerification: options.authenticatorSelection.userVerification as
            UserVerificationRequirement | undefined,
        }
      : undefined,
    attestation: options.attestation as AttestationConveyancePreference,
  };
};

/**
 * Complete passkey registration - sends credential response to Cognito
 */
export const completePasskeyRegistration = async (
  accessToken: string,
  credential: PublicKeyCredential
): Promise<void> => {
  const response = credential.response as AuthenticatorAttestationResponse;

  const registrationResponse = {
    id: credential.id,
    rawId: arrayBufferToBase64Url(credential.rawId),
    type: credential.type,
    response: {
      clientDataJSON: arrayBufferToBase64Url(response.clientDataJSON),
      attestationObject: arrayBufferToBase64Url(response.attestationObject),
      transports: response.getTransports?.() || [],
    },
    clientExtensionResults: credential.getClientExtensionResults(),
  };

  await cognitoApiCall('CompleteWebAuthnRegistration', {
    AccessToken: accessToken,
    Credential: registrationResponse,
  });
};

/**
 * Register a new passkey - combines start and complete
 */
export const registerPasskey = async (
  accessToken: string
): Promise<PublicKeyCredential> => {
  if (!isPasskeySupported()) {
    throw new Error('Passkeys are not supported in this browser');
  }

  // Get credential creation options from Cognito
  const options = await startPasskeyRegistration(accessToken);

  // Create credential using browser WebAuthn API
  const credential = (await navigator.credentials.create({
    publicKey: options,
  })) as PublicKeyCredential | null;

  if (!credential) {
    throw new Error('Failed to create passkey');
  }

  // Complete registration with Cognito
  await completePasskeyRegistration(accessToken, credential);

  return credential;
};

/**
 * List user's registered passkeys
 */
export const listPasskeys = async (
  accessToken: string
): Promise<PasskeyCredential[]> => {
  try {
    const response = await cognitoApiCall<{
      Credentials?: Array<{
        CredentialId: string;
        FriendlyCredentialName?: string;
        CreatedAt: number;
        AuthenticatorAttachment?: string;
      }>;
    }>('ListWebAuthnCredentials', {
      AccessToken: accessToken,
    });

    return (response.Credentials || []).map(c => ({
      credentialId: c.CredentialId,
      friendlyName: c.FriendlyCredentialName,
      createdAt: new Date(c.CreatedAt * 1000),
      authenticatorAttachment: c.AuthenticatorAttachment as
        'platform' | 'cross-platform' | undefined,
    }));
  } catch (error) {
    // If WebAuthn is not enabled, return empty array instead of throwing
    if (
      error instanceof Error &&
      error.message.includes('WebAuthn not enabled')
    ) {
      return [];
    }
    throw error;
  }
};

/**
 * Delete a registered passkey
 */
export const deletePasskey = async (
  accessToken: string,
  credentialId: string
): Promise<void> => {
  await cognitoApiCall('DeleteWebAuthnCredential', {
    AccessToken: accessToken,
    CredentialId: credentialId,
  });
};

/**
 * Initiate passkey sign-in - starts USER_AUTH flow with WEB_AUTHN challenge
 */
export const initiatePasskeySignIn = async (
  username?: string
): Promise<{
  session: string;
  credentialRequestOptions: PublicKeyCredentialRequestOptions;
}> => {
  const params: Record<string, unknown> = {
    AuthFlow: 'USER_AUTH',
    ClientId: cognitoConfig.clientId,
    AuthParameters: {
      PREFERRED_CHALLENGE: 'WEB_AUTHN',
    },
  };

  if (username) {
    params.AuthParameters = {
      ...(params.AuthParameters as Record<string, string>),
      USERNAME: username,
    };
  }

  const response = await cognitoApiCall<{
    ChallengeName?: string;
    Session?: string;
    ChallengeParameters?: {
      CREDENTIAL_REQUEST_OPTIONS?: string;
      AvailableChallenges?: string;
    };
    AuthenticationResult?: {
      IdToken: string;
      AccessToken: string;
      RefreshToken: string;
    };
  }>('InitiateAuth', params);

  // If Cognito returned tokens directly (no challenge), this user might not have passkeys
  if (response.AuthenticationResult) {
    throw new Error(
      'No passkey challenge received. You may not have any passkeys registered.'
    );
  }

  if (!response.ChallengeName || !response.Session) {
    throw new Error(
      'Invalid response from Cognito. WebAuthn may not be enabled for this user pool.'
    );
  }

  if (response.ChallengeName !== 'WEB_AUTHN') {
    // PASSWORD or SELECT_CHALLENGE means the user has no passkeys registered
    if (
      response.ChallengeName === 'PASSWORD' ||
      response.ChallengeName === 'SELECT_CHALLENGE'
    ) {
      throw new Error('NO_PASSKEYS_REGISTERED');
    }
    throw new Error(
      `Unexpected challenge type: ${response.ChallengeName}. Expected WEB_AUTHN.`
    );
  }

  if (!response.ChallengeParameters?.CREDENTIAL_REQUEST_OPTIONS) {
    throw new Error(
      'No credential request options received. WebAuthn may not be enabled for this user pool.'
    );
  }

  const credentialOptions = JSON.parse(
    response.ChallengeParameters.CREDENTIAL_REQUEST_OPTIONS
  );

  if (!credentialOptions.challenge) {
    throw new Error(
      'Invalid credential options received from Cognito. Missing challenge.'
    );
  }

  // Convert base64url strings to ArrayBuffer
  const publicKeyOptions: PublicKeyCredentialRequestOptions = {
    challenge: base64UrlToArrayBuffer(credentialOptions.challenge),
    timeout: credentialOptions.timeout,
    rpId: credentialOptions.rpId,
    userVerification: credentialOptions.userVerification as
      UserVerificationRequirement | undefined,
    allowCredentials: credentialOptions.allowCredentials?.map(
      (c: { id: string; type: string; transports?: string[] }) => ({
        id: base64UrlToArrayBuffer(c.id),
        type: c.type as PublicKeyCredentialType,
        transports: c.transports as AuthenticatorTransport[] | undefined,
      })
    ),
  };

  return {
    session: response.Session,
    credentialRequestOptions: publicKeyOptions,
  };
};

/**
 * Complete passkey sign-in - responds to WEB_AUTHN challenge
 */
export const completePasskeySignIn = async (
  session: string,
  credential: PublicKeyCredential,
  username?: string
): Promise<CognitoTokens> => {
  const response = credential.response as AuthenticatorAssertionResponse;

  const credentialResponse = {
    id: credential.id,
    rawId: arrayBufferToBase64Url(credential.rawId),
    type: credential.type,
    response: {
      clientDataJSON: arrayBufferToBase64Url(response.clientDataJSON),
      authenticatorData: arrayBufferToBase64Url(response.authenticatorData),
      signature: arrayBufferToBase64Url(response.signature),
      userHandle: response.userHandle
        ? arrayBufferToBase64Url(response.userHandle)
        : undefined,
    },
    clientExtensionResults: credential.getClientExtensionResults(),
  };

  const challengeResponses: Record<string, string> = {
    CREDENTIAL: JSON.stringify(credentialResponse),
  };

  if (username) {
    challengeResponses.USERNAME = username;
  }

  const authResponse = await cognitoApiCall<{
    AuthenticationResult: {
      IdToken: string;
      AccessToken: string;
      RefreshToken: string;
    };
  }>('RespondToAuthChallenge', {
    ChallengeName: 'WEB_AUTHN',
    ClientId: cognitoConfig.clientId,
    Session: session,
    ChallengeResponses: challengeResponses,
  });

  return {
    idToken: authResponse.AuthenticationResult.IdToken,
    accessToken: authResponse.AuthenticationResult.AccessToken,
    refreshToken: authResponse.AuthenticationResult.RefreshToken,
  };
};

/**
 * Sign in with passkey - combines initiate and complete
 * If no username provided, tries to use stored email from localStorage
 */
export const signInWithPasskey = async (
  username?: string
): Promise<CognitoTokens> => {
  if (!isPasskeySupported()) {
    throw new Error('Passkeys are not supported in this browser');
  }

  // If no username provided, try to get it from localStorage
  const effectiveUsername = username || getPasskeyEmail() || undefined;

  let session: string;
  let credentialRequestOptions: PublicKeyCredentialRequestOptions;

  try {
    // Initiate passkey sign-in
    const result = await initiatePasskeySignIn(effectiveUsername);
    session = result.session;
    credentialRequestOptions = result.credentialRequestOptions;
  } catch (error) {
    // Handle specific errors with user-friendly messages
    if (error instanceof Error) {
      if (
        error.message.includes('Missing required parameter USERNAME') ||
        error.message === 'NO_PASSKEYS_REGISTERED'
      ) {
        throw new Error('NO_PASSKEYS_REGISTERED');
      }
    }
    throw error;
  }

  // Get credential using browser WebAuthn API
  const credential = (await navigator.credentials.get({
    publicKey: credentialRequestOptions,
  })) as PublicKeyCredential | null;

  if (!credential) {
    throw new Error('Passkey authentication was cancelled');
  }

  // Complete sign-in
  const tokens = await completePasskeySignIn(
    session,
    credential,
    effectiveUsername
  );

  // Save email to localStorage for future usernameless sign-ins
  if (effectiveUsername) {
    savePasskeyEmail(effectiveUsername);
  }

  return tokens;
};

// ============= Base64URL Helpers =============

/**
 * Convert base64url string to ArrayBuffer
 */
const base64UrlToArrayBuffer = (base64url: string): ArrayBuffer => {
  // Add padding if needed
  const padding = '='.repeat((4 - (base64url.length % 4)) % 4);
  const base64 = base64url.replace(/-/g, '+').replace(/_/g, '/') + padding;
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
};

/**
 * Convert ArrayBuffer to base64url string
 */
const arrayBufferToBase64Url = (buffer: ArrayBuffer): string => {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  const base64 = btoa(binary);
  return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
};

// Export the user pool for advanced use cases
export { userPool, isCognitoConfigured };
